import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import api from '../api/axiosConfig';
import { useLocation, useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import { useTranslation } from 'react-i18next';
import OrderModal from '../components/OrderModal';
import PurchaseModal from '../components/PurchaseModal';

function getApiOrigin() {
  const baseURL = api?.defaults?.baseURL || '';

  if (!baseURL) {
    return window.location.origin;
  }

  try {
    const absoluteUrl = new URL(baseURL, window.location.origin);
    return absoluteUrl.origin;
  } catch (error) {
    console.error('Failed to parse api baseURL:', baseURL, error);
    return window.location.origin;
  }
}

const API_ORIGIN = getApiOrigin();

function toMediaUrl(url) {
  if (!url) return null;

  const clean = String(url).trim();
  if (!clean) return null;

  if (clean.startsWith('http://') || clean.startsWith('https://')) {
    return clean;
  }

  if (clean.startsWith('/')) {
    return `${API_ORIGIN}${clean}`;
  }

  if (clean.startsWith('media/')) {
    return `${API_ORIGIN}/${clean}`;
  }

  return `${API_ORIGIN}/media/${clean}`;
}

function safeApiPath(path) {
  const p = String(path || '');
  if (!p) return p;

  const normalized = p.replace(/\/{2,}/g, '/');

  if (normalized.startsWith('/api/')) return normalized.replace(/^\/api/, '');
  if (normalized.startsWith('api/')) return normalized.replace(/^api/, '');

  if (normalized === '/api') return '/';
  if (normalized === 'api') return '/';

  return normalized;
}

const Wrap = styled.div`
  width: min(100%, 1800px);
  margin: clamp(12px, 3vw, 28px) auto;
  display: grid;
  gap: 22px;
`;

const Top = styled.div`
  background: linear-gradient(180deg, #0b1b2b, #08131f);
  border-radius: 36px;
  padding: clamp(20px, 3vw, 36px);
  border: 1px solid rgba(255, 255, 255, 0.08);
`;

const Title = styled.h1`
  margin: 0;
  color: #fff;
  font-size: clamp(28px, 3.6vw, 46px);
  font-weight: 900;
`;

const Subtitle = styled.p`
  margin: 10px 0 0;
  color: rgba(255, 255, 255, 0.75);
  max-width: 600px;
`;

const Filters = styled.div`
  margin-top: 18px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const Select = styled.select`
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 14px;
  padding: 10px 14px;
  color: #fff;
  font-weight: 700;
  cursor: pointer;

  option {
    color: #000;
  }
`;

const FilterBtn = styled.button`
  border-radius: 14px;
  border: none;
  padding: 10px 16px;
  background: #ff8a1f;
  color: #111;
  font-weight: 900;
  cursor: pointer;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: clamp(14px, 2vw, 20px);

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: rgba(255, 255, 255, 0.95);
  border-radius: 22px;
  overflow: hidden;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.18);
  display: flex;
  flex-direction: column;
`;

const Preview = styled.div`
  aspect-ratio: 16 / 9;
  background: linear-gradient(135deg, #0b1b2b, #102c45);
  overflow: hidden;
  display: block;
`;

const PreviewImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const PreviewFallback = styled.div`
  height: 100%;
  display: grid;
  place-items: center;
  color: rgba(255, 255, 255, 0.35);
  font-weight: 900;
`;

const CardBody = styled.div`
  padding: 14px;
  display: grid;
  gap: 10px;
`;

const CardTitle = styled.div`
  font-weight: 900;
  color: #0b1b2b;
`;

const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const Tag = styled.span`
  font-size: 11px;
  padding: 5px 8px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.06);
  font-weight: 700;
`;

const PriceRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 6px;
`;

const Price = styled.div`
  font-weight: 900;
  color: #0b1b2b;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
`;

const Btn = styled.button`
  border-radius: 12px;
  padding: 8px 12px;
  border: none;
  cursor: pointer;
  font-weight: 800;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const DemoBtn = styled(Btn)`
  background: rgba(0, 0, 0, 0.08);
`;

const BuyBtn = styled(Btn)`
  background: #ff8a1f;
  color: #111;
`;

const CTA = styled.div`
  margin-top: 30px;
  border-radius: 36px;
  padding: 28px;
  background: linear-gradient(180deg, #0b1b2b, #08131f);
  text-align: center;
`;

const CTATitle = styled.h2`
  margin: 0;
  color: #fff;
  font-weight: 900;
`;

const CTASub = styled.p`
  color: rgba(255, 255, 255, 0.75);
`;

const CTAButton = styled.button`
  margin-top: 14px;
  border-radius: 16px;
  padding: 14px 22px;
  font-weight: 900;
  border: none;
  background: #ff8a1f;
  cursor: pointer;
`;

export default function Catalog() {
  const [orderOpen, setOrderOpen] = useState(false);
  const [purchaseProduct, setPurchaseProduct] = useState(null);
  const [brokenImages, setBrokenImages] = useState({});
  const { i18n } = useTranslation();
  const lang = i18n.language?.startsWith('it') ? 'it' : 'en';

  const pick = (obj, key, fallback = '') => {
    if (!obj) return fallback;
    return (lang === 'it' ? obj[`${key}_it`] : obj[`${key}_en`]) || obj[`${key}_en`] || fallback;
  };

  const [data, setData] = useState(null);
  const [cat, setCat] = useState('all');
  const [price, setPrice] = useState('all');
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    api
      .get(safeApiPath('/catalog/'))
      .then((res) => {
        console.log('Catalog response:', res.data);
        setData(res.data);
      })
      .catch((err) => {
        console.error('Catalog load error:', err);
      });
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const catParam = params.get('cat');
    const priceParam = params.get('price');

    if (catParam) setCat(catParam);
    if (priceParam) setPrice(priceParam);
  }, [location.search]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);

    if (cat && cat !== 'all') params.set('cat', cat);
    else params.delete('cat');

    if (price && price !== 'all') params.set('price', price);
    else params.delete('price');

    navigate({ search: params.toString() }, { replace: true });
  }, [cat, price]); // eslint-disable-line

  const products = data?.products || [];
  const page = data?.page;

  const filtered = useMemo(() => {
    return products.filter((p) => {
      const okCat = cat === 'all' || p.category?.slug === cat;

      const okPrice =
        price === 'all' ||
        (price === 'lt500' && p.price_from_eur < 500) ||
        (price === '500_1000' && p.price_from_eur >= 500 && p.price_from_eur <= 1000) ||
        (price === 'gt1000' && p.price_from_eur > 1000);

      return okCat && okPrice;
    });
  }, [products, cat, price]);

  const seoTitle = pick(page, 'title', 'Catalog');
  const seoDesc = pick(
    page,
    'subtitle',
    'Ready-to-use website templates: landing pages, corporate sites, marketplaces.',
  );

  return (
    <Wrap>
      <SEO title={seoTitle} description={seoDesc} path="/catalog" />

      <Top>
        <Title>{pick(page, 'title', 'Catalog')}</Title>
        <Subtitle>{pick(page, 'subtitle', 'Ready-to-use website templates')}</Subtitle>

        <Filters>
          <Select value={cat} onChange={(e) => setCat(e.target.value)}>
            <option value="all">{pick(page, 'filter_category_label', 'All categories')}</option>
            {(data?.categories || []).map((c) => (
              <option key={c.id} value={c.slug}>
                {c.name}
              </option>
            ))}
          </Select>

          <Select value={price} onChange={(e) => setPrice(e.target.value)}>
            <option value="all">{pick(page, 'filter_price_label', 'Price')}</option>
            <option value="lt500">500€</option>
            <option value="500_1000">500–1000€</option>
            <option value="gt1000">1000€+</option>
          </Select>

          <FilterBtn type="button" onClick={() => {}}>
            {pick(page, 'filter_button_text', 'Filter')}
          </FilterBtn>
        </Filters>
      </Top>

      <Grid>
        {filtered.map((p) => {
          const imgUrl = toMediaUrl(p.image_thumb || p.image);
          const isBroken = brokenImages[p.id];

          return (
            <Card key={p.id}>
              <Preview>
                {imgUrl && !isBroken ? (
                  <PreviewImg
                    src={imgUrl}
                    alt={p.title}
                    loading="lazy"
                    decoding="async"
                    onError={() => {
                      console.error('Image failed to load:', {
                        productId: p.id,
                        title: p.title,
                        image_thumb: p.image_thumb,
                        image: p.image,
                        resolvedUrl: imgUrl,
                      });
                      setBrokenImages((prev) => ({ ...prev, [p.id]: true }));
                    }}
                  />
                ) : (
                  <PreviewFallback>
                    {pick(page, 'preview_fallback', 'Preview')}
                  </PreviewFallback>
                )}
              </Preview>

              <CardBody>
                <CardTitle>{p.title}</CardTitle>

                <Tags>
                  {(p.tags || []).map((t) => (
                    <Tag key={t.id}>{t.name}</Tag>
                  ))}
                </Tags>

                <PriceRow>
                  <Price>
                    {p.price_from_eur != null
                      ? `${pick(page, 'price_from_label', 'from')} ${p.price_from_eur}€`
                      : '—'}
                  </Price>

                  <Actions>
                    <DemoBtn
                      disabled={!p.demo_url}
                      onClick={() =>
                        p.demo_url && window.open(p.demo_url, '_blank', 'noopener,noreferrer')
                      }
                    >
                      {pick(page, 'demo_button_text', 'View demo')}
                    </DemoBtn>

                    <BuyBtn
                      onClick={() => setPurchaseProduct(p)}
                    >
                      {pick(page, 'buy_button_text', 'Buy')}
                    </BuyBtn>
                  </Actions>
                </PriceRow>
              </CardBody>
            </Card>
          );
        })}
      </Grid>

      <CTA onClick={() => setOrderOpen(true)}>
        <CTATitle>{pick(page, 'cta_title', 'Need a custom website?')}</CTATitle>
        <CTASub>{pick(page, 'cta_subtitle', 'I will create...')}</CTASub>
        <CTAButton>{pick(page, 'cta_button_text', 'Get a quote')}</CTAButton>
      </CTA>

      <OrderModal open={orderOpen} onClose={() => setOrderOpen(false)} />

      <PurchaseModal
        open={!!purchaseProduct}
        product={purchaseProduct}
        onClose={() => setPurchaseProduct(null)}
        onCreated={(order) => {
          alert(`Order #${order.order_number} created and awaiting approval.`);
        }}
      />
    </Wrap>
  );
}
